/**
 * The one deployment-environment label used by every reporting surface.
 *
 * Why this exists: before 2026-09-20 nothing in the stack recorded WHERE an
 * event or row came from. `tool_runs` (migration 081) had no environment
 * column, `radar_leads` had none, and the PostHog project filter excluded
 * `localhost` / `127.0.0.1` and nothing else — so preview deployments and
 * production sat in one undifferentiated pile. The 2026-09-19 analytics audit
 * could not separate them, which is why no growth number in it was defensible.
 *
 * Resolution order (server): VERCEL_ENV -> NODE_ENV -> 'development'.
 * Resolution order (browser): NEXT_PUBLIC_VERCEL_ENV -> 'development' when
 * NODE_ENV says development -> otherwise 'unknown'.
 *
 * The browser deliberately does NOT fall back to NODE_ENV for a deployed
 * build. `next build` sets NODE_ENV=production for EVERY build, previews
 * included, so that fallback would stamp `env: 'production'` on every event
 * from a preview deployment the moment `NEXT_PUBLIC_VERCEL_ENV` went missing
 * — silently recreating the exact contamination this module exists to
 * prevent. An honest 'unknown' is worth more than a confident wrong label.
 *
 * Vercel automatically injects the framework-prefixed `NEXT_PUBLIC_VERCEL_ENV`
 * for Next.js on production and preview deployments, so in practice the
 * browser gets a real value; 'unknown' is the safety net for when it does not.
 * The server keeps its NODE_ENV fallback because VERCEL_ENV is always present
 * on Vercel, making that path unreachable in a real deployment.
 *
 * This label is NOT a trust boundary and must never gate access, billing, or
 * credit decisions — it is a reporting dimension. Server rows get the server
 * value (authoritative); browser events get the client value (advisory).
 */

export const ENVIRONMENTS = ['production', 'preview', 'development'] as const

export type Environment = (typeof ENVIRONMENTS)[number]

/**
 * The browser can legitimately fail to determine its deployment. Kept as a
 * separate type so 'unknown' can never reach the server-side writers, whose
 * `environment` columns are CHECK-constrained to the three real values
 * (migration 192).
 */
export type ClientEnvironment = Environment | 'unknown'

function coerce(value: string | undefined): Environment | null {
  if (!value) return null
  const normalized = value.trim().toLowerCase()
  return (ENVIRONMENTS as readonly string[]).includes(normalized)
    ? (normalized as Environment)
    : null
}

/**
 * Server-side environment. Safe to call from route handlers, telemetry, and
 * any module that never reaches the browser bundle.
 */
export function resolveServerEnvironment(): Environment {
  return (
    coerce(process.env.VERCEL_ENV) ??
    coerce(process.env.NODE_ENV) ??
    'development'
  )
}

/**
 * Browser-side environment. `NEXT_PUBLIC_VERCEL_ENV` is inlined at build time,
 * so this is a build-time constant per deployment — which is what we want: it
 * describes the deployment that served the page, not the request.
 */
export function resolveClientEnvironment(): ClientEnvironment {
  const configured = coerce(process.env.NEXT_PUBLIC_VERCEL_ENV)
  if (configured) return configured

  // Only NODE_ENV=development is trustworthy here — it means a local dev
  // server, never a deployed build. Every other NODE_ENV value on the client
  // is 'production' by construction and says nothing about the deployment.
  if (process.env.NODE_ENV === 'development') return 'development'

  return 'unknown'
}
